/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable, inject } from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  Validators,
} from '@angular/forms';
import {
  IBoton,
  ICategoria,
  ICategoriaCliente,
  ICliente,
  ICreateBoton,
  ICreateCategoria,
  ICreateCliente,
  IPlantillaBotonera,
  IReemplazoCategoriaCliente,
} from 'modelos/src';
import { firstValueFrom } from 'rxjs';
import {
  ID_PLANTILLA_MANUAL,
  limpiarPlantilla,
  reemplazarBoton,
} from '../../../auxiliares/plantillas/plantilla-manual';
import { BotonesService } from '../../botones/botones.service';
import { CategoriasService } from '../../categorias/categorias.service';
import { ClientesService } from '../clientes.service';
import { crearGrupoBoton } from '../provisionador/boton-form/boton-form.component';
import {
  CategoriaEditable,
  categoriaCambio,
  esTmpBtn,
  huellaCategoria,
  SLOTS_LEGACY,
  SLOTS_NATIVOS,
  TMP_CAT,
} from './cliente-form.model';

/** Datos para guardar el cliente completo (cliente + sus categorías + sus botones nuevos). */
export interface GuardarClienteOpts {
  /** `_id` del cliente si es edición; ausente = creación. */
  idCliente?: string;
  /** Cliente tal como vino del backend (para decidir si el PUT hace falta). */
  original?: ICliente;
  /** Valor del form raíz, ya limpio de vacíos. Sin `categorias` ni los ids de categoría. */
  datos: ICreateCliente;
  /** Categorías del cliente, en orden. */
  categorias: CategoriaEditable[];
  /** Puede ser un `tmp-cat-N` todavía sin resolver. */
  idCategoriaDefault: string;
  categoriasDefault: { desde: number; hasta: number; idCategoria: string }[];
  /** true si el form raíz está pristine Y el polígono no cambió. */
  clientePristine: boolean;
  /**
   * Categorías que esta edición saca del cliente, con la categoría a la que pasan sus vecinos.
   * Los ids pueden ser `tmp-cat-N` (por ejemplo al clonar): se resuelven acá antes del PUT.
   */
  reemplazosCategorias?: IReemplazoCategoriaCliente[];
}

/** Entidades creadas en este intento, para poder deshacerlas si algo falla. */
interface Creado {
  botones: string[];
  categorias: string[];
}

/** Una categoría ya persistida, con lo necesario para reapuntar sus ids temporales. */
interface CategoriaPersistida {
  ed: CategoriaEditable;
  /** Id que tenía en el form (puede ser `tmp-cat-N`). */
  idViejo: string;
  /** Id real en la base. */
  idReal: string;
  /** `tmp-btn-N` → `_id` real de los botones que se crearon. */
  mapaBtn: Map<string, string>;
}

export interface ResultadoGuardar {
  cliente: ICliente;
  /** Id viejo (posiblemente temporal) → id real de categoría. Para reapuntar el form. */
  mapaCat: Map<string, string>;
}

/**
 * Orquesta el guardado del form unificado de cliente y construye las `CategoriaEditable`.
 *
 * Reemplaza, en un solo lugar, lo que hoy está repartido entre `ProvisionadorService.provisionar`
 * (crear todo de cero con rollback) y `CrearEditarClienteComponent.guardarCategorias` (editar las
 * categorías del cliente en edición). El orden es siempre el mismo: primero las categorías (el
 * cliente necesita `idCategoriaDefault`), después el cliente.
 *
 * A futuro esto se puede reemplazar por una ruta backend transaccional cambiando solo `guardar`.
 */
@Injectable({ providedIn: 'root' })
export class ClienteFormService {
  private fb = inject(UntypedFormBuilder);
  private botonesService = inject(BotonesService);
  private categoriasService = inject(CategoriasService);
  private clientesService = inject(ClientesService);

  private contadorTmpCat = 0;

  // ============================ Factory de CategoriaEditable ============================

  private grupoCategoria(cat?: Partial<ICategoria>): any {
    return this.fb.group({
      nombre: [cat?.nombre ?? null, Validators.required],
      variante: [cat?.variante ?? null],
      // El backend exige prioridad (Mongoose required).
      prioridad: [cat?.prioridad ?? 1, Validators.required],
      color: [cat?.color ?? null],
      inicioTemplate: [cat?.inicioTemplate ?? null],
      nombreTemplate: [cat?.nombreTemplate ?? null],
      // Plantilla armada a mano (solo cuenta con inicioTemplate === 'manual'). Un solo control con
      // el objeto entero: el editor lo reemplaza en cada cambio y la huella lo ve en `grupo.value`.
      plantillaBotonera: [cat?.plantillaBotonera ?? null],
    });
  }

  private grupoImagenesNativas(cc?: ICategoriaCliente): any {
    const g: Record<string, any[]> = {};
    for (const [slot, campo] of Object.entries(SLOTS_NATIVOS)) {
      g[slot] = [cc?.[campo] ?? null];
    }
    return this.fb.group(g);
  }

  private grupoImagenesLegacy(cc?: ICategoriaCliente): any {
    const g: Record<string, any[]> = {};
    for (const s of SLOTS_LEGACY) {
      g[s.control] = [cc?.[s.campo] ?? null];
    }
    return this.fb.group(g);
  }

  /**
   * Un botón no puede estar en dos pantallas a la vez: si viene repetido (dato viejo), se queda en
   * la primera. Sin esto habría dos controles con el mismo `_tmpId` y el `track` de Angular
   * rompería, además de crearse el botón dos veces al guardar.
   */
  private dedupePantallas(p1: string[], p2: string[], p3: string[]): void {
    const vistos = new Set<string>();
    for (const arr of [p1, p2, p3]) {
      for (let i = arr.length - 1; i >= 0; i--) {
        if (vistos.has(arr[i])) arr.splice(i, 1);
      }
      for (const id of arr) vistos.add(id);
    }
  }

  /**
   * Arma el FormArray de botones de una categoría a partir de sus tres listas de ids, resolviendo
   * cada id contra el catálogo. Los ids reales entran como botón EXISTENTE (referencia).
   */
  private arrayBotones(
    ids: string[],
    porId: Map<string, IBoton>,
  ): UntypedFormArray {
    const arr = this.fb.array([]) as UntypedFormArray;
    for (const id of ids) {
      arr.push(crearGrupoBoton(this.fb, id, porId.get(id) ?? { _id: id }));
    }
    return arr;
  }

  /** Categoría que YA está en el cliente: se edita la entidad global (con aviso si es compartida). */
  public desdeCliente(
    cc: ICategoriaCliente,
    cat: ICategoria | undefined,
    porId: Map<string, IBoton>,
    compartidaCon: string[],
  ): CategoriaEditable {
    const p1 = [...(cat?.idsBtnsPrincipales ?? [])];
    const p2 = [...(cat?.idsBtnsSecundarios ?? [])];
    const p3 = [...(cat?.idsOtrosBotones ?? [])];
    this.dedupePantallas(p1, p2, p3);
    const ed: CategoriaEditable = {
      idCategoria: cc.idCategoria!,
      nueva: false,
      grupo: this.grupoCategoria(cat),
      botones: this.arrayBotones([...p1, ...p2, ...p3], porId),
      idsPantalla1: p1,
      idsPantalla2: p2,
      idsPantalla3: p3,
      imagenes: this.grupoImagenesNativas(cc),
      imagenesLegacy: this.grupoImagenesLegacy(cc),
      snapshot: '',
      compartidaCon,
    };
    ed.snapshot = huellaCategoria(ed);
    return ed;
  }

  /**
   * Copia propia de una categoría existente: mismos datos y misma botonera, y los botones se
   * REFERENCIAN (no se duplican). Se crea al guardar, así la original queda intacta.
   */
  public duplicar(
    cat: ICategoria,
    porId: Map<string, IBoton>,
    sufijoVariante: string,
  ): CategoriaEditable {
    const p1 = [...(cat.idsBtnsPrincipales ?? [])];
    const p2 = [...(cat.idsBtnsSecundarios ?? [])];
    const p3 = [...(cat.idsOtrosBotones ?? [])];
    this.dedupePantallas(p1, p2, p3);
    const grupo = this.grupoCategoria(cat);
    // La variante es justamente lo que distingue dos categorías con el mismo nombre.
    grupo.patchValue({ variante: sufijoVariante || cat.variante || 'copia' });
    const ed: CategoriaEditable = {
      idCategoria: `${TMP_CAT}${++this.contadorTmpCat}`,
      nueva: true,
      grupo,
      botones: this.arrayBotones([...p1, ...p2, ...p3], porId),
      idsPantalla1: p1,
      idsPantalla2: p2,
      idsPantalla3: p3,
      imagenes: this.grupoImagenesNativas(),
      imagenesLegacy: this.grupoImagenesLegacy(),
      snapshot: '',
      compartidaCon: [],
    };
    ed.snapshot = huellaCategoria(ed);
    return ed;
  }

  /**
   * Categoría en blanco. El preset solo elige la botonera de arranque:
   * `basica` = un botón grande centrado, `completa` = grilla de 3 pantallas.
   *
   * `clienteCustom` = el cliente tiene template hecho a mano en la app: entonces la categoría
   * arranca SIN botonera propia (null), porque en el nativo la categoría gana sobre el cliente y
   * una genérica le reemplazaría el layout.
   */
  public enBlanco(
    preset: 'basica' | 'completa',
    nombreSugerido: string,
    clienteCustom = false,
  ): CategoriaEditable {
    const grupo = this.grupoCategoria({
      nombre: nombreSugerido,
      prioridad: 1,
      inicioTemplate: clienteCustom
        ? undefined
        : preset === 'basica'
          ? 'centrado'
          : 'grid',
    });
    const ed: CategoriaEditable = {
      idCategoria: `${TMP_CAT}${++this.contadorTmpCat}`,
      nueva: true,
      grupo,
      botones: this.fb.array([]) as UntypedFormArray,
      idsPantalla1: [],
      idsPantalla2: [],
      idsPantalla3: [],
      imagenes: this.grupoImagenesNativas(),
      imagenesLegacy: this.grupoImagenesLegacy(),
      snapshot: '',
      compartidaCon: [],
    };
    ed.snapshot = huellaCategoria(ed);
    return ed;
  }

  /**
   * Clona una categoría COMPARTIDA que ya está en el cliente, arrastrando lo que el operador venía
   * editando (incluidas las imágenes override). La original no se toca.
   */
  public clonarEditable(
    ed: CategoriaEditable,
    sufijoVariante: string,
  ): CategoriaEditable {
    const grupo = this.grupoCategoria(ed.grupo.value);
    grupo.patchValue({
      variante: sufijoVariante || ed.grupo.get('variante')?.value || 'copia',
    });
    const clon: CategoriaEditable = {
      idCategoria: `${TMP_CAT}${++this.contadorTmpCat}`,
      nueva: true,
      grupo,
      botones: this.fb.array([]) as UntypedFormArray,
      idsPantalla1: [...ed.idsPantalla1],
      idsPantalla2: [...ed.idsPantalla2],
      idsPantalla3: [...ed.idsPantalla3],
      imagenes: this.grupoImagenesNativas(),
      imagenesLegacy: this.grupoImagenesLegacy(),
      snapshot: '',
      compartidaCon: [],
    };
    // Mismos botones (mismas referencias / mismos borradores) que la que se venía editando.
    for (const g of ed.botones.controls as any[]) {
      clon.botones.push(
        crearGrupoBoton(this.fb, String(g.get('_tmpId')?.value), g.value),
      );
    }
    clon.imagenes.patchValue(ed.imagenes.value);
    clon.imagenesLegacy.patchValue(ed.imagenesLegacy.value);
    clon.imagenes.markAsDirty();
    clon.snapshot = huellaCategoria(clon);
    return clon;
  }

  // ============================ Uso por cliente ============================

  /**
   * Mapa `idCategoria` → nombres de los clientes que la referencian, excluyendo al cliente que se
   * está editando. Es lo que alimenta el badge "compartida con N clientes". Se calcula en el front
   * a partir del listado de clientes: no hace falta endpoint nuevo.
   */
  public usoPorCategoria(
    clientes: ICliente[],
    idClienteActual?: string,
  ): Record<string, string[]> {
    const out: Record<string, string[]> = {};
    for (const c of clientes) {
      if (c._id === idClienteActual) continue;
      for (const cc of c.categorias ?? []) {
        if (!cc.idCategoria) continue;
        (out[cc.idCategoria] ??= []).push(c.nombre ?? c._id ?? '');
      }
    }
    return out;
  }

  // ============================ Guardado ============================

  /** Botón (form) → `ICreateBoton`. Mismo subconjunto que usa el provisionador. */
  private armarBoton(v: any): ICreateBoton {
    return {
      nombre: v.nombre,
      variante: v.variante,
      funcion: v.funcion,
      urlImagen: v.urlImagen || undefined,
      urlImagenPush: v.urlImagenPush || undefined,
      texto: v.texto || undefined,
      color: v.color || undefined,
      grupoDestino: v.grupoDestino || undefined,
      link: v.link || undefined,
      telemedicina: v.telemedicina || undefined,
      sirena: v.sirena || undefined,
      global: v.global || undefined,
      prioridad: v.prioridad ?? undefined,
      notificacion: v.notificacion || undefined,
      codigoSmartCity: v.codigoSmartCity || undefined,
    };
  }

  /** Overrides de imagen de la categoría → campos de `ICategoriaCliente` (solo los cargados). */
  private imagenesDe(ed: CategoriaEditable): Partial<ICategoriaCliente> {
    const out: Record<string, string> = {};
    const nativas = ed.imagenes.value as Record<string, string | null>;
    for (const [slot, campo] of Object.entries(SLOTS_NATIVOS)) {
      if (nativas[slot]) out[campo] = nativas[slot] as string;
    }
    const legacy = ed.imagenesLegacy.value as Record<string, string | null>;
    for (const s of SLOTS_LEGACY) {
      if (legacy[s.control]) out[s.campo] = legacy[s.control] as string;
    }
    return out as Partial<ICategoriaCliente>;
  }

  /**
   * Crea los botones nuevos de la categoría y después crea o edita la entidad `Categoria`.
   * Los botones existentes van por referencia: NO se editan acá (se editan en el CRUD de Botones).
   * Devuelve el `_id` real de la categoría y el mapa de botones creados.
   */
  private async persistirCategoria(
    ed: CategoriaEditable,
    creado: Creado,
  ): Promise<{ idReal: string; mapaBtn: Map<string, string> }> {
    const mapaBtn = new Map<string, string>();
    for (const g of ed.botones.controls as any[]) {
      const v = g.value;
      const tmp = String(v._tmpId);
      if (esTmpBtn(tmp)) {
        const r = await firstValueFrom(
          this.botonesService.crear(this.armarBoton(v)),
        );
        creado.botones.push(r._id!);
        mapaBtn.set(tmp, r._id!);
      } else {
        mapaBtn.set(tmp, tmp);
      }
    }
    const resolver = (ids: string[]): string[] =>
      ids.map((t) => mapaBtn.get(t)).filter((id): id is string => !!id);

    const v = ed.grupo.value;
    const dato: ICreateCategoria = {
      nombre: v.nombre,
      variante: v.variante || undefined,
      prioridad: v.prioridad ?? 1,
      color: v.color || undefined,
      // Vacio => `null` explicito, NO `undefined`: `undefined` no se serializa y el
      // findByIdAndUpdate de datos deja el valor viejo, o sea que la plantilla no se puede
      // sacar una vez puesta (la categoria nunca vuelve a heredar la del cliente/bundleId).
      inicioTemplate: v.inicioTemplate || (null as unknown as string),
      nombreTemplate: v.nombreTemplate || (null as unknown as string),
      // Solo con la plantilla manual elegida; si no, `null` para no dejar guardada una que nadie ve.
      plantillaBotonera:
        v.inicioTemplate === ID_PLANTILLA_MANUAL
          ? limpiarPlantilla(v.plantillaBotonera, (id) => mapaBtn.get(id))
          : (null as unknown as IPlantillaBotonera),
      idsBtnsPrincipales: resolver(ed.idsPantalla1),
      idsBtnsSecundarios: resolver(ed.idsPantalla2),
      idsOtrosBotones: resolver(ed.idsPantalla3),
    };

    if (ed.nueva) {
      const cat = await firstValueFrom(this.categoriasService.crear(dato));
      creado.categorias.push(cat._id!);
      return { idReal: cat._id!, mapaBtn };
    }
    if (categoriaCambio(ed)) {
      await firstValueFrom(this.categoriasService.editar(ed.idCategoria, dato));
    }
    return { idReal: ed.idCategoria, mapaBtn };
  }

  /**
   * Deja los `CategoriaEditable` apuntando a los ids REALES y con la huella al día. Sin esto, un
   * segundo Guardar volvería a crear las categorías y los botones que este intento acaba de crear
   * (los `tmp-` seguirían en el form). Se llama SOLO cuando todo el guardado salió bien: si algo
   * falla, el rollback borra esas entidades y los `tmp-` tienen que seguir siendo `tmp-`.
   */
  private reapuntarIds(persistidas: CategoriaPersistida[]): void {
    for (const { ed, idReal, mapaBtn } of persistidas) {
      ed.idCategoria = idReal;
      ed.nueva = false;
      for (const g of ed.botones.controls as any[]) {
        const real = mapaBtn.get(String(g.get('_tmpId')?.value));
        if (real) g.patchValue({ _tmpId: real }, { emitEvent: false });
      }
      const reapuntar = (arr: string[]): void => {
        for (let i = 0; i < arr.length; i++) {
          const real = mapaBtn.get(arr[i]);
          if (real) arr[i] = real;
        }
      };
      reapuntar(ed.idsPantalla1);
      reapuntar(ed.idsPantalla2);
      reapuntar(ed.idsPantalla3);
      // La plantilla manual también referencia botones por id.
      let plantilla = ed.grupo.get('plantillaBotonera')?.value ?? null;
      if (plantilla) {
        for (const [tmp, real] of mapaBtn) {
          if (tmp !== real) plantilla = reemplazarBoton(plantilla, tmp, real);
        }
        ed.grupo.patchValue(
          { plantillaBotonera: plantilla },
          { emitEvent: false },
        );
      }
      ed.snapshot = huellaCategoria(ed);
    }
  }

  /** Huella estable (orden de campos fijo) de `cliente.categorias`, para comparar antes/después. */
  private huellaCatsCliente(cats?: ICategoriaCliente[]): string {
    const campos = [
      'idCategoria',
      ...Object.values(SLOTS_NATIVOS),
      ...SLOTS_LEGACY.map((s) => s.campo),
    ];
    return JSON.stringify(
      (cats ?? []).map((c) => campos.map((k) => (c as any)[k] ?? null)),
    );
  }

  /** ¿Cambió algo de la parte "categorías" del cliente respecto de lo que había guardado? */
  private cambioParteCategorias(
    original: ICliente | undefined,
    datos: ICreateCliente,
  ): boolean {
    if (
      this.huellaCatsCliente(original?.categorias) !==
      this.huellaCatsCliente(datos.categorias)
    ) {
      return true;
    }
    if (
      (original?.idCategoriaDefault ?? null) !==
      (datos.idCategoriaDefault ?? null)
    ) {
      return true;
    }
    return (
      JSON.stringify(original?.categoriasDefault ?? []) !==
      JSON.stringify(datos.categoriasDefault ?? [])
    );
  }

  /**
   * Guarda todo: primero las categorías (crear las nuevas / editar las tocadas), después el
   * cliente. Si algo falla, borra las entidades que ESTE intento creó (las ediciones de
   * categorías ya existentes no se deshacen: modo de falla aceptado, igual que en el
   * provisionador). Las imágenes ya subidas a GCS quedan huérfanas (no hay endpoint de borrado).
   */
  public async guardar(opts: GuardarClienteOpts): Promise<ResultadoGuardar> {
    const creado: Creado = { botones: [], categorias: [] };
    try {
      const persistidas: CategoriaPersistida[] = [];
      for (const ed of opts.categorias) {
        const idViejo = ed.idCategoria;
        const { idReal, mapaBtn } = await this.persistirCategoria(ed, creado);
        persistidas.push({ ed, idViejo, idReal, mapaBtn });
      }
      const mapaCat = new Map(persistidas.map((p) => [p.idViejo, p.idReal]));
      const resolver = (id?: string): string | undefined =>
        id ? (mapaCat.get(id) ?? id) : undefined;

      const datos: ICreateCliente = {
        ...opts.datos,
        categorias: persistidas.map((p) => ({
          idCategoria: p.idReal,
          ...this.imagenesDe(p.ed),
        })),
        idCategoriaDefault: resolver(opts.idCategoriaDefault),
        categoriasDefault: opts.categoriasDefault
          .filter((r) => !!r.idCategoria)
          .map((r) => ({ ...r, idCategoria: resolver(r.idCategoria)! })),
      };

      // El backend exige un reemplazo por cada categoría que sale del cliente y tenga vecinos
      // con ella vigente. El destino puede ser una categoría recién creada (el clon), así que
      // se resuelve con el mismo mapa que el resto de las referencias.
      const reemplazosCategorias = (opts.reemplazosCategorias ?? []).map(
        (r) => ({
          idCategoria: resolver(r.idCategoria)!,
          idCategoriaReemplazo: r.idCategoriaReemplazo
            ? resolver(r.idCategoriaReemplazo)!
            : null,
        }),
      );

      let cliente: ICliente;
      if (!opts.idCliente) {
        cliente = await firstValueFrom(this.clientesService.crear(datos));
      } else if (
        // Un PUT con el form sin cambios puede pisar campos que actualizó otro proceso
        // (ej. los bundleIds del build): si no cambió nada, no se manda.
        opts.clientePristine &&
        !this.cambioParteCategorias(opts.original, datos)
      ) {
        cliente = opts.original!;
      } else {
        cliente = await firstValueFrom(
          this.clientesService.editar(opts.idCliente, {
            ...datos,
            ...(reemplazosCategorias.length ? { reemplazosCategorias } : {}),
          }),
        );
      }

      this.reapuntarIds(persistidas);
      return { cliente, mapaCat };
    } catch (err) {
      await this.rollback(creado);
      throw err;
    }
  }

  /**
   * Borra lo creado en este intento, en orden inverso. Ignora errores de cleanup.
   *
   * Va por `eliminarConReemplazos` con la lista vacía y no por el DELETE viejo: son categorías
   * recién creadas, así que no tienen vecinos y no hay nada que reasignar. Con el DELETE viejo
   * el cleanup fallaría en silencio (`safe` se come el error) y quedarían categorías basura.
   */
  private async rollback(creado: Creado): Promise<void> {
    for (const id of [...creado.categorias].reverse()) {
      await this.safe(() =>
        firstValueFrom(this.categoriasService.eliminarConReemplazos(id, [])),
      );
    }
    for (const id of [...creado.botones].reverse()) {
      await this.safe(() => firstValueFrom(this.botonesService.eliminar(id)));
    }
  }

  private async safe(fn: () => Promise<unknown>): Promise<void> {
    try {
      await fn();
    } catch (err) {
      console.error('Rollback parcial falló (se ignora):', err);
    }
  }
}
